// src/components/workspace/investigation/RunbookPanel.js

import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { Info, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { db } from "../../../firebase";
import { getRunbookForCategory } from "../../../constants/runbooks";
import {
  INCIDENT_EVENTS,
  recordIncidentEvent,
} from "../../../services/incidentEventService";

/**
 * The diagnostic procedure for this incident category, plus anything the expert
 * adds on the way.
 *
 * Steps are stored in the existing checklist subcollection with deterministic
 * document IDs (runbook__<stepId>), which makes seeding idempotent — both
 * participants can have the workspace open without creating duplicates.
 */

const RUNBOOK_PREFIX = "runbook__";

export default function RunbookPanel({
  problemId,
  problem,
  currentUser,
  viewerRole,
}) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [newItem, setNewItem] = useState("");
  const seededRef = useRef(false);

  const runbook = useMemo(
    () => getRunbookForCategory(problem?.categoryId),
    [problem?.categoryId]
  );

  useEffect(() => {
    if (!problemId) return undefined;
    const unsubscribe = onSnapshot(
      collection(db, "problems", problemId, "checklist"),
      (snapshot) => {
        setItems(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoaded(true);
      },
      (error) => {
        console.error("Runbook subscription error:", error);
        setLoaded(true);
      }
    );
    return unsubscribe;
  }, [problemId]);

  // The expert's workspace seeds the standard procedure once, so a session never
  // starts from a blank checklist. Deterministic IDs make this safe to retry.
  useEffect(() => {
    if (!loaded || seededRef.current) return;
    if (viewerRole !== "expert") return;

    const hasRunbookItems = items.some((item) =>
      item.id.startsWith(RUNBOOK_PREFIX)
    );
    if (hasRunbookItems) {
      seededRef.current = true;
      return;
    }

    seededRef.current = true;
    Promise.all(
      runbook.steps.map((step, index) =>
        setDoc(
          doc(db, "problems", problemId, "checklist", `${RUNBOOK_PREFIX}${step.id}`),
          {
            text: step.label,
            hint: step.hint || null,
            source: "runbook",
            order: index,
            completed: false,
            createdBy: currentUser.uid,
            createdByName: currentUser.displayName || currentUser.email,
            createdAt: serverTimestamp(),
          },
          { merge: true }
        )
      )
    ).catch((error) => {
      console.error("Could not seed runbook:", error);
    });
  }, [loaded, items, runbook, problemId, currentUser, viewerRole]);

  const { runbookItems, adHocItems, completedCount } = useMemo(() => {
    const fromRunbook = items
      .filter((i) => i.id.startsWith(RUNBOOK_PREFIX))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const adHoc = items
      .filter((i) => !i.id.startsWith(RUNBOOK_PREFIX))
      .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
    return {
      runbookItems: fromRunbook,
      adHocItems: adHoc,
      completedCount: items.filter((i) => i.completed).length,
    };
  }, [items]);

  const totalCount = items.length;
  const progress = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;

  const handleToggle = async (item) => {
    const nextCompleted = !item.completed;
    try {
      await updateDoc(doc(db, "problems", problemId, "checklist", item.id), {
        completed: nextCompleted,
        completedAt: nextCompleted ? serverTimestamp() : null,
      });

      if (nextCompleted) {
        await recordIncidentEvent(problemId, {
          type: INCIDENT_EVENTS.RUNBOOK_STEP_COMPLETED,
          actor: {
            id: currentUser.uid,
            name: currentUser.displayName || currentUser.email,
            role: viewerRole,
          },
          summary: item.text,
          metadata: {
            stepId: item.id.replace(RUNBOOK_PREFIX, ""),
            source: item.source || "ad-hoc",
          },
        });
      }
    } catch (error) {
      console.error("Could not update step:", error);
      toast.error("Could not update that step.");
    }
  };

  const handleAdd = async () => {
    const text = newItem.trim();
    if (!text) return;
    try {
      await setDoc(
        doc(collection(db, "problems", problemId, "checklist")),
        {
          text,
          source: "ad-hoc",
          completed: false,
          createdBy: currentUser.uid,
          createdByName: currentUser.displayName || currentUser.email,
          createdAt: serverTimestamp(),
        }
      );
      setNewItem("");
    } catch (error) {
      console.error("Could not add step:", error);
      toast.error("Could not add that step.");
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await deleteDoc(doc(db, "problems", problemId, "checklist", itemId));
    } catch (error) {
      console.error("Could not delete step:", error);
      toast.error("Could not remove that step.");
    }
  };

  const renderItem = (item) => (
    <li
      key={item.id}
      className="group flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2.5 transition hover:border-slate-300"
    >
      <input
        type="checkbox"
        checked={Boolean(item.completed)}
        onChange={() => handleToggle(item)}
        className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
        aria-label={item.text}
      />
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm ${
            item.completed ? "text-slate-400 line-through" : "text-slate-800"
          }`}
        >
          {item.text}
        </p>
        {item.hint && !item.completed && (
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            {item.hint}
          </p>
        )}
        {item.completed && item.createdByName && (
          <p className="mt-0.5 text-xs text-slate-400">
            checked by {item.createdByName}
          </p>
        )}
      </div>
      {item.source !== "runbook" && (
        <button
          type="button"
          onClick={() => handleDelete(item.id)}
          className="flex-shrink-0 rounded p-1 text-slate-300 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 focus:opacity-100 group-hover:opacity-100"
          aria-label={`Remove step: ${item.text}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );

  return (
    <section className="flex h-full min-h-0 flex-col">
      <div className="flex-shrink-0 border-b border-slate-200 px-4 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-slate-900">
              {runbook.title}
            </h2>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
              {runbook.intent}
            </p>
          </div>
          <span className="flex-shrink-0 text-xs font-semibold tabular-nums text-slate-600">
            {completedCount}/{totalCount}
          </span>
        </div>
        {totalCount > 0 && (
          <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {!loaded ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-lg bg-slate-100"
              />
            ))}
          </div>
        ) : totalCount === 0 ? (
          <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
            <p className="text-xs leading-relaxed text-slate-600">
              The expert will load the standard diagnostic procedure for this
              category when they begin.
            </p>
          </div>
        ) : (
          <>
            <ul className="list-none space-y-2">
              {runbookItems.map(renderItem)}
            </ul>

            {adHocItems.length > 0 && (
              <>
                <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Added during this incident
                </p>
                <ul className="list-none space-y-2">
                  {adHocItems.map(renderItem)}
                </ul>
              </>
            )}
          </>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd();
          }}
          placeholder="Add a step to this investigation"
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-800 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!newItem.trim()}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </div>
    </section>
  );
}
